(() => {
  if (customElements.get('premium-hero')) return;
  class PremiumHero extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      if (!this.slides.length) return;
      this.index = 0;
      this.visible = true;
      this.hovered = false;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.running = this.dataset.autoplay === 'true' && !this.reducedMotion.matches &&
        document.documentElement.dataset.premiumMotion !== 'none' && !window.Shopify?.designMode;
      this.toggle = this.querySelector('[data-toggle]');
      this.dots = [...this.querySelectorAll('[data-go]')];
      this.controls = this.querySelector('[data-controls]');
      if (this.controls) this.controls.hidden = false;
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-toggle')) {
          this.running = !this.running;
          if (this.running) this.hovered = false;
          this.syncRotation();
        } else if (button.hasAttribute('data-go')) {
          this.go(Number(button.dataset.go), true);
        } else if (button.hasAttribute('data-step')) {
          this.go(this.index + Number(button.dataset.step), true);
        }
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (/INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) return;
        const rtl = getComputedStyle(this).direction === 'rtl';
        let next;
        if (event.key === 'ArrowRight') next = this.index + (rtl ? -1 : 1);
        if (event.key === 'ArrowLeft') next = this.index + (rtl ? 1 : -1);
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.slides.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.go(next, true);
      }, { signal });
      this.addEventListener('focusin', () => this.stopRotation(), { signal });
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.hovered = true;
        this.syncRotation();
      }, { signal });
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.syncRotation();
      }, { signal });
      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch' || event.target.closest('a, button')) return;
        this.pointerStart = { x: event.clientX, y: event.clientY };
        this.stopRotation();
      }, { signal, passive: true });
      this.addEventListener('pointerup', (event) => {
        if (!this.pointerStart) return;
        const dx = event.clientX - this.pointerStart.x;
        const dy = event.clientY - this.pointerStart.y;
        this.pointerStart = null;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        const rtl = getComputedStyle(this).direction === 'rtl';
        this.go(this.index + (dx < 0 ? 1 : -1) * (rtl ? -1 : 1), true);
      }, { signal, passive: true });
      this.addEventListener('pointercancel', () => { this.pointerStart = null; }, { signal });
      document.addEventListener('visibilitychange', () => {
        this.syncVideos();
        this.syncRotation();
      }, { signal });
      this.reducedMotion.addEventListener('change', () => this.stopRotation(), { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => slide.contains(event.target) ||
          slide.dataset.blockId === event.detail?.blockId);
        if (index >= 0) this.go(index, true);
      }, { signal });
      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.syncVideos();
        this.syncRotation();
      }, { threshold: 0.05 });
      this.observer.observe(this);
      this.querySelectorAll('video').forEach((video) => {
        video.addEventListener('error', () => {
          video.hidden = true;
          console.warn('[Premium hero] Video failed to load; showing poster.', video.error);
        }, { signal });
      });
      this.go(0);
    }
    stopRotation() {
      this.running = false;
      this.syncRotation();
    }
    stop() {
      this.stopRotation();
    }
    go(index, interacted = false) {
      if (interacted) this.running = false;
      const previous = this.slides[this.index];
      const moveFocus = previous?.contains(document.activeElement);
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== this.index;
        slide.inert = i !== this.index;
        slide.classList.toggle('is-entering', i === this.index && previous !== slide);
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      const current = this.querySelector('[data-current]');
      if (current) current.textContent = String(this.index + 1).padStart(2, '0');
      const status = this.querySelector('[data-status]');
      if (status && interacted) status.textContent = this.slides[this.index].getAttribute('aria-label');
      if (moveFocus && previous !== this.slides[this.index]) {
        this.slides[this.index].querySelector('a, button')?.focus();
      }
      this.syncVideos();
      this.syncRotation();
    }
    syncVideos() {
      const shouldPlay = this.visible && !document.hidden;
      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (shouldPlay && i === this.index) {
          video.hidden = false;
          const promise = video.play();
          promise?.catch((error) => {
            if (!this.isConnected || error.name === 'AbortError') return;
            video.hidden = true;
            console.warn('[Premium hero] Video playback unavailable; showing poster.', error);
          });
        } else {
          video.pause();
        }
      });
    }
    syncRotation() {
      clearTimeout(this.timer);
      const active = this.running && this.visible && !document.hidden && !this.hovered;
      if (this.toggle) {
        this.toggle.setAttribute('aria-pressed', String(this.running));
        this.toggle.setAttribute('aria-label', this.running ? this.dataset.pauseLabel : this.dataset.playLabel);
      }
      if (active && this.slides.length > 1) {
        this.timer = setTimeout(() => this.go(this.index + 1), Math.max(5000, Number(this.dataset.interval) || 7000));
      }
    }
    sync() {
      this.syncVideos();
      this.syncRotation();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      this.querySelectorAll('video').forEach((video) => video.pause());
    }
  }
  customElements.define('premium-hero', PremiumHero);
})();
